import { useCallback, useState } from "react";
import { api, ApiClientError } from "../../lib/apiClient.js";
import type { ProductDto } from "@grizzly/shared";
import { useI18n } from "../../i18n/index.js";
import { Button } from "../ui/button.js";
import { Modal } from "../ui/modal.js";
import { SelectInput, TextInput } from "../ui/field.js";
import { useToast } from "../../lib/useToast.js";

export function ProductDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  onCreated(product: ProductDto): void;
}) {
  const { push } = useToast();
  const { t } = useI18n();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState("equipment");
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      const res = await api.createProduct({ code, name, kind });
      push(t("catalog.product.addedToast"), name);
      setCode("");
      setName("");
      onOpenChange(false);
      onCreated(res.product);
    } catch (err) {
      if (err instanceof ApiClientError) push(err.message, undefined, "danger");
    } finally {
      setBusy(false);
    }
  }, [code, kind, name, onCreated, onOpenChange, push, t]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("catalog.product.addTitle")}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={busy || !code.trim() || !name.trim()}
          >
            {busy ? t("common.saving") : t("catalog.product.saved")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <TextInput
          label={t("common.fields.code")}
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        <TextInput
          label={t("common.fields.name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <SelectInput
          label={t("common.fields.kind")}
          options={[
            { value: "equipment", label: t("status.product.equipment") },
            { value: "fabricated", label: t("status.product.fabricated") },
            { value: "component", label: t("status.product.component") },
            { value: "service", label: t("status.product.service") },
          ]}
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        />
      </div>
    </Modal>
  );
}
